import { guardEmptyArray, guardUnspecified } from '@portal/utils/util-guards';
import type { Regions } from '@smh/projects/regions';
import AngryVoteIcon from 'jtn-ui/svg/reactions/angry-vote.svg';
import HappyVoteIcon from 'jtn-ui/svg/reactions/happy-vote.svg';
import LikeVoteIcon from 'jtn-ui/svg/reactions/like-vote.svg';
import SadVoteIcon from 'jtn-ui/svg/reactions/sad-vote.svg';
import SurprisedVoteIcon from 'jtn-ui/svg/reactions/surprised-vote.svg';
import { componentFactoryOf } from 'vue-tsx-support';

import { JtnUiTypography } from '@jtnews/jtn-ui';
import {
  injectStylesMixin,
  debounce,
  tsStoreMixin,
  ObserverVisibility
} from '@jtnews/shared';

import styles from './record-voting.styles.scss?module';

interface Events {
  onVoted: EventVote;
}

type EventVote = {
  name: string;
  title: string;
};

type IconType = 'angry' | 'happy' | 'like' | 'surprised' | 'sad';

type Vote = {
  count: number;
  name: IconType;
  title: string;
};

type ComponentData = {
  obsHalfVoteOptions: ObserverVisibility;
  isFirstMount: boolean;
  activeVote: string;
  votes: Vote[];
};

type RecordData = {
  id: number;
};

const EMOJI_SIZE = 24;

const fieldTypes = {
  vote: 'Оценка материала',
  share: 'Шаринг(Внизу)',
  tags: 'Теги'
};

export default componentFactoryOf<Events>()
  .mixin(tsStoreMixin)
  .mixin(injectStylesMixin(styles))
  .create({
    name: 'RecordVoting',
    props: {
      isMobile: {
        type: Boolean,
        default: false
      },
      items: {
        type: Array as () => Vote[]
      }
    },
    data(): ComponentData {
      return {
        obsHalfVoteOptions: new ObserverVisibility('half'),
        isFirstMount: true,
        activeVote: '',
        votes: []
      };
    },
    computed: {
      recordData(): RecordData {
        const recordBlock = this.store.layoutModule ? this.store.layoutModule.record : {};
        return recordBlock && recordBlock.data ? recordBlock.data : {};
      },
      recordId(): number {
        return this.recordData.id;
      },
      regionId(): Regions {
        return this.store.regionId;
      }
    },
    created() {
      if (guardUnspecified(this.items)) {
        this.votes = [...this.items];
      }
    },
    mounted() {
      this.$nextTick(() => {
        this.calcTooltipPositions();
      });
      this.obsHalfVoteOptions.event.subscribe(event => {
        if (event.value) {
          this.sendNewReachGoal('Оценка материала', 'Просмотр', 'viewRating');
        }
      });
    },
    methods: {
      sendNewReachGoal(
        fieldType: string,
        valueName: string | Record<string, string>,
        goalName: string
      ) {
        const blockType = 'Центральная колонка';
        this.store.analyticsModule.sendNewReachGoal({
          blockType,
          fieldType,
          valueName,
          goalName
        });
      },
      sendNewFormatReachGoal(
        goalName: string,
        blockType: string,
        actionType: string,
        prop1?: string,
        prop2?: string
      ) {
        this.store.analyticsModule.sendNewFormatReachGoal({
          goalName,
          blockType,
          actionType,
          prop1,
          prop2
        });
      },
      setRecordVote(name: IconType, title: string) {
        if (this.activeVote) {
          const activeVoteTitle = this.votes.find(v => v.name === this.activeVote)?.title;
          this.sendNewReachGoal(
            fieldTypes.vote,
            { Переголосовал: `Поменял ${activeVoteTitle} на ${title}` },
            'clickRe_Rating'
          );
        } else {
          this.sendNewReachGoal(fieldTypes.vote, { Проголосовал: title }, 'clickRating');
        }
        this.sendNewFormatReachGoal('ClickArticleFooter', 'Оценка', 'Клик', name);
        // отправляем значение и инкриментим в не зависимости от того, что пришлет БЕ
        this.sendVote(name);
        this.countVotes(name);
      },
      sendVote(vote: IconType): void {
        void this.store.newsApiClient.recordVote(this.recordId, {
          regionId: this.regionId,
          vote
        });
      },
      countVotes(name: string) {
        this.votes = this.votes.map(item => {
          let increment = 0;
          if (item.name === name) {
            increment++;
          }

          if (item.name === this.activeVote) {
            increment--;
          }

          return Object.assign(item, { count: item.count + increment });
        });

        this.activeVote = name;
      },
      calcTooltipPositions() {
        const elements = this.$refs.votes as Element[];
        if (guardUnspecified(elements) && guardEmptyArray(elements)) {
          elements.forEach(vote => {
            // eslint-disable-next-line prefer-destructuring
            const tooltip = vote.children[1];
            const tooltipWidth = tooltip.clientWidth;
            const voteWidth = vote.clientWidth;
            const left = (tooltipWidth - voteWidth) / 2;
            tooltip.setAttribute('style', `left: ${-left}px`);
          });
        }
      },
      voting(vote: Vote) {
        this.isFirstMount = false;
        const { name, title } = vote;
        if (this.activeVote !== name) {
          this.setRecordVote(name, title);
        }
        if (this.isMobile) {
          this.debouncedFunc(() => this.removeActiveClass(styles.voteTooltip), 1000)();
        }
      },
      removeActiveClass(selector: string) {
        const active = this.votes.findIndex(vote => vote.name === this.activeVote);
        const node = (this.$refs.votes as Element[])[active].querySelector(
          `.${selector}`
        );

        node?.classList.remove(styles.active);
      },
      debouncedFunc(func: (...args: unknown[]) => unknown, ms: number) {
        return debounce(func, ms);
      },
      getIcon(name: IconType) {
        switch (name) {
          case 'angry':
            return (
              <img src={AngryVoteIcon} alt="" width={EMOJI_SIZE} height={EMOJI_SIZE} />
            );

          case 'happy':
            return (
              <img src={HappyVoteIcon} alt="" width={EMOJI_SIZE} height={EMOJI_SIZE} />
            );
          case 'like':
            return (
              <img src={LikeVoteIcon} alt="" width={EMOJI_SIZE} height={EMOJI_SIZE} />
            );
          case 'surprised':
            return (
              <img
                src={SurprisedVoteIcon}
                alt=""
                width={EMOJI_SIZE}
                height={EMOJI_SIZE}
              />
            );
          case 'sad':
            return (
              <img src={SadVoteIcon} alt="" width={EMOJI_SIZE} height={EMOJI_SIZE} />
            );

          default:
            break;
        }
      }
    },
    render() {
      return (
        <section
          v-observe-visibility={this.obsHalfVoteOptions.getOptions('half')}
          class={styles.recordVoting}
        >
          <ul class={styles.votes}>
            {this.votes.map(vote => (
              <li
                ref="votes"
                refInFor={true}
                key={vote.name}
                class={[
                  styles.vote,
                  vote.name === this.activeVote ? styles.active : '',
                  vote.count === 0 ? styles.zeroVote : ''
                ]}
                onClick={() => this.voting(vote)}
              >
                {this.getIcon(vote.name)}
                <JtnUiTypography
                  class={[
                    styles.voteTooltip,
                    vote.name === this.activeVote && !this.isFirstMount
                      ? styles.active
                      : ''
                  ]}
                  tag="span"
                  type="font-online-marker"
                >
                  {vote.title}
                </JtnUiTypography>
                <JtnUiTypography
                  class={styles.counter}
                  tag="span"
                  type="font-vijet-type-3"
                >
                  {vote.count}
                </JtnUiTypography>
              </li>
            ))}
          </ul>
        </section>
      );
    }
  });
